
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import Head from 'next/head';
import { useRouter } from 'next/router';

import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { FUTURE } from 'timeline/enums/constants';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';

const TP = 'pages.timeline';

const TimelineYear = ({
  constructorResponse,
  globalPricingId,
  timelineBreadcrumbs,
  isCrawler = false,
}) => {
  const { query } = useRouter();

  const breadcrumb = timelineBreadcrumbs?.find(
    (yearBreadcrumb) => yearBreadcrumb.name === query?.timelineYear?.[0],
  );

  const { pageTitle: futureTitleWithCountryCode, pageDescription: futurePageDescription } =
    usePageMetadata({
      defaultTitle: t(`${TP}.futureTitle`, 'Upcoming Sneaker Releases'),
      constructorResponse,
    });

  const { pageTitle: titleWithCountryCode, pageDescription } = usePageMetadata({
    defaultTitle: t(`${TP}.title`, 'Sneaker Release Calendar'),
    constructorResponse,
    mainTitle: breadcrumb?.titleTag,
    mainDescription: breadcrumb?.metadataDescription,
  });

  const isFuture = query?.timelineYear?.[0] === FUTURE;
  const {
    response: { collection },
  } = constructorResponse;

  const browseKey = collection ? 'collection_id' : 'group_id';
  const browseValue = collection ? collection.id : 'sneakers';

  return (
    <>
      <Head>
        {isFuture ? (
          <>
            <title>{futureTitleWithCountryCode}</title>
            <meta
              name="description"
              content={
                futurePageDescription ||
                t(
                  `${TP}.futureDescription`,
                  'Stay up to date on the latest sneaker drops and upcoming sneaker releases from Air Jordan, Yeezy, Nike, and more.',
                )
              }
              key="description"
            />
          </>
        ) : (
          <>
            <title>{titleWithCountryCode}</title>
            <meta
              name="description"
              content={
                pageDescription ||
                t(
                  `${TP}.description`,
                  'Shop new sneaker releases and iconic styles from Air Jordan, Nike, Yeezy and more on GOAT, the global platform for the greatest products from the past, present and future.',
                )
              }
              key="description"
            />
          </>
        )}
      </Head>
      <SearchGrid
        browseKey={browseKey}
        browseValue={browseValue}
        constructorResponse={constructorResponse}
        timelineBreadcrumbs={timelineBreadcrumbs}
        globalPricingId={globalPricingId}
        isCrawler={isCrawler}
      />
    </>
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const pathname = resolvedUrl?.split('?')?.[0];
  const { timelineYear }: { timelineYear: string } = query;
  const isCrawler = isSearchCrawler(req?.headers);
  if (!timelineYear && pathname !== '/timeline') {
    return { notFound: true };
  }

  try {
    // future and present are not using constructor collection, and is just a 'release_date' filter
    const isFutureOrPresent = timelineYear?.[0] === FUTURE || pathname === '/timeline';
    const browseKey = isFutureOrPresent ? 'group_id' : 'collection_id';
    const browseValue = isFutureOrPresent ? 'sneakers' : `timeline-${timelineYear}`;
    const globalPricingId = await fetchGlobalPricingId({ req, res });

    let breadcrumbs = {};

    try {
      const breadcrumbsResponse: string | ICMSBreadcrumb = await sneakersRequest(req, res)
        .get(`api/v1/breadcrumbs/${BREADCRUMBS.TIMELINE}/years?includeChildren=true`)
        .then((response) => response.body);

      if (typeof breadcrumbsResponse !== 'string') {
        breadcrumbs = breadcrumbsResponse?.children;
      }
    } catch (error) {
      bugsnag(error, { context: 'Error fetching Timeline breadcrumbs' });
    }

    const browseResponse = await getBrowseResults({
      req,
      res,
      resolvedUrl,
      query,
      filterKey: browseKey,
      filterValue: browseValue,
    });

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        constructorResponse: browseResponse,
        globalPricingId,
        timelineBreadcrumbs: breadcrumbs,
        isCrawler,
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default TimelineYear;
